/**
 * Мелкие визуальные элементы макета: бейдж приоритета, чип стадии, дата срока,
 * чипы-факты, дельты и чекбокс чек-листа.
 *
 * Все классы — из `base.css`, новых имён здесь нет, кроме `.stage-track`
 * (хлебные крошки стадий; в макете их не было, разметка согласована в
 * `docs/spec/ui-case-card.md` §4.5 и добавлена в `styles/index.css`).
 */

import type { ReactNode } from 'react';

import { deadlineClass, leftText, type DeadlineState } from '../lib/deadlines';
import { delta as computeDelta, type DateLike } from '../lib/format';
import { stageChipClass, stageLabel, stageSteps, turnTypeLabel } from '../lib/stages';
import type { Stage, TurnType } from '../api/types';

/* ─────────────────────────── Приоритет ─────────────────────────── */

/** Бейдж есть только у приоритетов 1 и 2; для 3–5 макет ничего не рисует. */
export function PriorityBadge({ priority }: { priority: number | null | undefined }) {
    if (priority === 1) return <span className="badge p1">П1</span>;
    if (priority === 2) return <span className="badge p2">П2</span>;
    return null;
}

/* ─────────────────────────── Стадия ─────────────────────────── */

export function StageChip({ stage, label }: { stage: Stage | string | null; label?: string }) {
    if (!stage) return null;
    return <span className={stageChipClass(stage)}>{label ?? stageLabel(stage)}</span>;
}

/**
 * Хлебные крошки стадии для карточки дела. Вставляются между `.modal-actions`
 * и `.modal-tabs` — на светлой полосе, иначе цвета `.stage-chip` не читаются на тёмном фоне.
 */
export function StageTrack({ stage }: { stage: Stage | string | null }) {
    const steps = stageSteps(stage);
    return (
        <ol className="stage-track" aria-label="Стадия рассмотрения">
            {steps.map((step) => (
                <li
                    key={step.stage}
                    className={'st-step' + (step.state === 'todo' ? '' : ' ' + step.state)}
                    aria-current={step.state === 'current' ? 'step' : undefined}
                >
                    <span className={stageChipClass(step.stage)}>{step.label}</span>
                </li>
            ))}
        </ol>
    );
}

/* ─────────────────────────── Срок ─────────────────────────── */

export type DeadlineCarrier = 'item-date' | 'my-date' | 'lr-date' | 'dr-date';

export interface DeadlineDateProps {
    /** Носитель определяет размер и контекст; цвет во всех одинаковый. */
    base?: DeadlineCarrier;
    deadline?: DateLike;
    /** `days_left` из API; если не передан — считается по дате. */
    daysLeft?: number | null;
    /** `deadline_state` из API; если не передан — выводится из `daysLeft`. */
    state?: DeadlineState;
    /** `deadline_text` из API; если не передан — собирается `leftText`. */
    text?: string;
    /** Продлённый срок красится охрой, но только когда дело не горит и не просрочено. */
    extended?: boolean;
    children?: ReactNode;
}

export function DeadlineDate({
    base = 'item-date',
    deadline,
    daysLeft,
    state,
    text,
    extended,
    children,
}: DeadlineDateProps) {
    const modifier = state ? (state === 'ok' ? '' : state) : deadlineClass(daysLeft);
    const extMod = !modifier && extended && base === 'my-date' ? 'ext' : '';
    const className = [base, modifier || extMod].filter(Boolean).join(' ');
    return <span className={className}>{children ?? text ?? leftText(deadline, daysLeft)}</span>;
}

/** «В работе» / «Просрочено» / «Закрыто» — бейдж состояния дела. */
export function StatusBadge({
    stage,
    daysLeft,
}: {
    stage: Stage | string | null;
    daysLeft?: number | null;
}) {
    if (stage === 'sent') return <span className="badge closed">Закрыто</span>;
    if (daysLeft !== undefined && daysLeft !== null && daysLeft < 0) {
        return <span className="badge late">Просрочено</span>;
    }
    return <span className="badge open">В работе</span>;
}

/** Значки в теме дела: 🛡 риск проверки, 🔁 повторное, ⏳ продлено. */
export function CaseFlags({
    escalationRisk,
    repeat,
    extended,
}: {
    escalationRisk?: boolean;
    repeat?: boolean;
    extended?: boolean;
}) {
    return (
        <>
            {escalationRisk && (
                <span className="flag" title="Риск проверки" aria-label="Риск проверки">
                    🛡
                </span>
            )}
            {repeat && (
                <span className="flag" title="Повторное обращение" aria-label="Повторное обращение">
                    🔁
                </span>
            )}
            {extended && (
                <span className="flag" title="Срок продлён" aria-label="Срок продлён">
                    ⏳
                </span>
            )}
        </>
    );
}

/* ─────────────────────────── Чипы и метки ─────────────────────────── */

export type ChipTone = 'neutral' | 'bad' | 'good' | 'warn';

export function Chip({ tone = 'neutral', children }: { tone?: ChipTone; children: ReactNode }) {
    return <span className={tone === 'neutral' ? 'chip' : 'chip ' + tone}>{children}</span>;
}

/** «на сейчас» — снимок открытых дел, «за период» — пересчитывается под интервал. */
export function ScopeTag({ scope }: { scope: 'now' | 'period' }) {
    return scope === 'period' ? (
        <span className="scope-tag period">за период</span>
    ) : (
        <span className="scope-tag">на сейчас</span>
    );
}

export function TurnBadge({ turnType }: { turnType: TurnType | string | null }) {
    if (!turnType) return null;
    return <span className="turn-badge">{turnTypeLabel(turnType)}</span>;
}

/* ─────────────────────────── Дельты ─────────────────────────── */

/**
 * Крупная дельта: `▲ +3` красным, `▼ -2` зелёным, `➡ 0` серым.
 * Направление намеренно инвертировано — рост числа жалоб это плохо.
 */
export function Delta({
    cur,
    prev,
    detail,
}: {
    cur: number;
    prev: number;
    /** «(было 25 → стало 34)» — печатается серым рядом. */
    detail?: ReactNode;
}) {
    const d = computeDelta(cur, prev);
    return (
        <span className="change">
            <span className={d.direction}>{d.text}</span>
            {detail !== undefined && <span className="detail"> {detail}</span>}
        </span>
    );
}

/** Компактная дельта для числовой колонки: `+3` / `-2` / `0`. */
export function DeltaSmall({ cur, prev }: { cur: number; prev: number }) {
    const d = computeDelta(cur, prev);
    return <span className={'delta ' + d.direction}>{d.shortText}</span>;
}

/* ─────────────────────────── Чекбокс ─────────────────────────── */

export interface CheckBoxProps {
    checked: boolean;
    onChange?: (next: boolean) => void;
    disabled?: boolean;
    /** Подсказка при `disabled` — в макете «Отметки ставит исполнитель». */
    title?: string;
}

/**
 * Галочка всегда в DOM и переключается прозрачностью (`.check-box svg { opacity:0 }`),
 * условный рендер SVG ломает плавный переход.
 */
export function CheckBox({ checked, onChange, disabled, title }: CheckBoxProps) {
    return (
        <button
            type="button"
            className={checked ? 'check-box on' : 'check-box'}
            disabled={disabled}
            title={title}
            aria-pressed={checked}
            aria-label={checked ? 'Снять отметку' : 'Отметить выполненным'}
            onClick={() => onChange?.(!checked)}
        >
            <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="#fff"
                strokeWidth="3.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        </button>
    );
}
